import { useExportToExcelMutation, useLazyGetBlobsQuery } from '@consigli/hooks';
import { DocumentPackage } from '@consigli/types';
import { MIME_TYPES, saveBlobBase64 } from '@consigli/utils';
import { useCallback, useMemo, useState } from 'react';

import { errorToast } from '@/components/toast';
import { createBlobsExcelExport } from '@/utils/excel-export';

/**
 * Hook for downloading all documents in a package as an Excel file.
 */
export const useDownloadDocumentsExcel = () => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [getBlobs, { isFetching }] = useLazyGetBlobsQuery();
  const [exportToExcel] = useExportToExcelMutation();

  const downloadPackageAsExcel = useCallback(
    async (documentPackage: DocumentPackage, projectId: number) => {
      setIsDownloading(true);
      try {
        const result = await getBlobs({
          projectId,
          packageId: documentPackage.id,
          page: 'all',
        }).unwrap();

        const packageName = documentPackage.name ?? 'files';
        const excelExport = createBlobsExcelExport(result.results, packageName);
        const { base64Data } = await exportToExcel({
          projectId,
          packageId: documentPackage.id,
          excelExport,
        }).unwrap();

        saveBlobBase64(`${packageName}.xlsx`, base64Data, MIME_TYPES.xlsx);
      } catch (error) {
        const message = error instanceof Error ? error.message : 'An unexpected error occurred';
        errorToast({ message: `Something went wrong: ${message}` });
      } finally {
        setIsDownloading(false);
      }
    },
    [exportToExcel, getBlobs],
  );

  const isLoading = useMemo(() => isFetching || isDownloading, [isFetching, isDownloading]);

  return {
    downloadPackageAsExcel,
    isDownloading: isLoading,
  };
};
